import React from "react";
import {
  AccountTreeOutlined,
  ContentCopyOutlined,
  KeyboardArrowRight,
  PlayArrowOutlined,
} from "@mui/icons-material";
import { Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from "@mui/material";
import WorkflowCanvasIcon from "./WorkflowCanvasIcon";
import { WORKFLOW_CANVAS_LABELS } from "./workflowBuilderData";
import { WorkflowLibraryItem } from "./workflowLibraryModel";
import "./WorkflowDefinitionCard.scss";

export type WorkflowCardAction = "Run" | "Duplicate" | "Open";

interface WorkflowDefinitionCardProps {
  definition: WorkflowLibraryItem;
  onAction: (action: WorkflowCardAction, definition: WorkflowLibraryItem) => void;
  /** Hidden rather than disabled — each depends on a grant (or a published version) the user
   *  either has or doesn't, so a dead button would only invite a click that can't work. */
  canRun: boolean;
  canDuplicate: boolean;
  canOpen: boolean;
  /** This card's duplicate is in flight — blocks a second copy from a double click. */
  isDuplicating?: boolean;
}

const WorkflowDefinitionCard: React.FC<WorkflowDefinitionCardProps> = ({
  definition,
  onAction,
  canRun,
  canDuplicate,
  canOpen,
  isDuplicating = false,
}) => {
  const handleRun = () => onAction("Run", definition);
  const handleDuplicate = () => onAction("Duplicate", definition);
  const handleOpen = () => onAction("Open", definition);

  const { startStageType } = definition;

  return (
    <Card component="article" className="workflow-card" variant="outlined">
      {/* Stretched hit sits above the body copy and under Run/Duplicate/Open so the card opens
          without nesting those buttons inside another button. */}
      {canOpen && (
        <button
          type="button"
          className="workflow-card__hit"
          onClick={handleOpen}
          aria-label={`Open ${definition.name}`}
        />
      )}
      <CardContent className="workflow-card__content">
        {/* Tile and name share the top row; the pills sit on their own row below so the name
            still gets everything left of the card edge instead of ~110px. */}
        <Box className="workflow-card__header">
          {startStageType ? (
            <Box
              className={`workflow-card__stage-icon workflow-card__stage-icon--${startStageType}`}
              aria-label={`First stage: ${WORKFLOW_CANVAS_LABELS[startStageType]}`}
            >
              <WorkflowCanvasIcon type={startStageType} />
            </Box>
          ) : (
            // A blank draft has no start stage yet (or the API doesn't say) — a neutral tile
            // rather than a guessed stage colour.
            <Box
              className="workflow-card__stage-icon workflow-card__stage-icon--none"
              aria-label="No stages yet"
            >
              <AccountTreeOutlined />
            </Box>
          )}

          <Box className="workflow-card__heading">
            <Typography component="h2" className="workflow-card__title">
              {/* Still clamped at two lines so an unusually long name can't stretch the grid row;
                  the full text stays reachable through the tooltip. */}
              <span className="workflow-card__title-text" title={definition.name}>
                {definition.name}
              </span>
            </Typography>
            <Typography className="workflow-card__category">
              {definition.category} workflow
            </Typography>
          </Box>
        </Box>

        <Box className="workflow-card__badges">
          <Chip
            className={`workflow-card__status workflow-card__status--${definition.status}`}
            label={
              <>
                <span className="workflow-card__status-dot" aria-hidden="true" />
                {definition.status}
              </>
            }
            size="small"
          />
        </Box>

        {definition.description && (
          <Typography className="workflow-card__description">{definition.description}</Typography>
        )}

        <Box className="workflow-card__meta" aria-label="Workflow details">
          <span>
            Version <strong>v{definition.version}</strong>
          </span>
          <span>
            Stages <strong>{definition.stageCount}</strong>
          </span>
          {definition.activeInstanceCount !== null && (
            <span>
              Active <strong>{definition.activeInstanceCount}</strong>
            </span>
          )}
          <span>
            Edited <strong>{definition.editedOn ?? "—"}</strong>
          </span>
        </Box>

        <Box className="workflow-card__actions">
          {canRun && (
            <Button
              className="workflow-card__action"
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<PlayArrowOutlined />}
              onClick={handleRun}
            >
              Run
            </Button>
          )}
          {canDuplicate && (
            <Button
              className="workflow-card__action"
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={
                isDuplicating ? <CircularProgress size={14} color="inherit" /> : <ContentCopyOutlined />
              }
              onClick={handleDuplicate}
              disabled={isDuplicating}
            >
              Duplicate
            </Button>
          )}
          {canOpen && (
            <Button
              className="workflow-card__action workflow-card__action--open"
              variant="contained"
              size="small"
              endIcon={<KeyboardArrowRight />}
              onClick={handleOpen}
            >
              Open
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default WorkflowDefinitionCard;
